import { useCallback, useEffect, useState } from 'react'
import { CreditCardData, CreditCardValidate } from '@open-tender/types'
import { useCreditCardForm } from '@open-tender/utils'
import {
  addCustomerCreditCard,
  resetCustomerCreditCardsError,
  selectBrand,
  selectConfig,
  selectCustomerCreditCards,
  selectRecaptcha,
  useAppDispatch,
  useAppSelector,
} from '@open-tender/cloud'
import { ButtonSubmit, CreditCard, FormError, FormSubmit } from 'components'
import { sendAddPaymentInfo } from 'app/analytics'
import FreedomPay from '../FreedomPay'
import styled from '@emotion/styled'
import { initKount } from 'utils/helpers'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'

export const FreedomPayContainer = styled(FreedomPay)`
  margin-top: 1.2rem;
`

const makeErrMsg = (formError: string | undefined) => {
  if (!formError || formError === 'Missing Customer') return null
  return formError.includes('parameters')
    ? 'There are one or more errors below'
    : formError
}

const CreditCardForm = ({
  windowRef,
  callback,
  submitText = 'Add New Card',
  submittingText = 'Authorizing Card...',
  revenue_center_id = null,
  RCrequire_cardholder_name,
}: {
  windowRef: React.RefObject<HTMLDivElement>
  callback: (cvv?: string) => void
  submitText?: string
  submittingText?: string
  revenue_center_id: number | null
  RCrequire_cardholder_name: boolean
}) => {
  const [kountSessionId, setKountSessionId] = useState<string | null>(null)
  const [mlSessionId, setMlSessionId] = useState<string | null>(null)
  const { executeRecaptcha } = useGoogleReCaptcha()
  const dispatch = useAppDispatch()
  const { settings } = useAppSelector(selectConfig) || {}
  const kount = settings?.kount
  const { require_cardholder_name: brandRequireCardholderName = false } =
    useAppSelector(selectBrand) || {}
  const { addCard: includeRecaptcha } = useAppSelector(selectRecaptcha) || {
    addCard: false,
  }
  const getRecaptchaToken = async () =>
    (await executeRecaptcha?.('addCard')) || null

  const isCardholderNameRequired = revenue_center_id
    ? RCrequire_cardholder_name
    : brandRequireCardholderName

  const { loading, error } = useAppSelector(selectCustomerCreditCards)

  const addCard = useCallback(
    (data: CreditCardData, callback: (cvv?: string | number) => void) =>
      dispatch(
        addCustomerCreditCard({
          data,
          callback: () => {
            const last4 = data?.acct?.slice(-4) || ''
            // format: cvv,last4
            if (callback) callback(`${data?.cvv},${last4}`)
            sendAddPaymentInfo()
          },
        })
      ),
    [dispatch]
  )
  const [newCard, setNewCard] = useState<
    (CreditCardValidate & { isComplete: boolean }) | null
  >(null)
  const {
    card: cardData = null,
    cardType = null,
    // isComplete = false,
    // errors: cardErrors,
  } = newCard || {}

  const { submitRef, errors, submitting, handleSubmit } = useCreditCardForm(
    loading,
    error,
    cardData,
    cardType,
    addCard,
    callback,
    includeRecaptcha,
    revenue_center_id,
    kountSessionId,
    mlSessionId,
    isCardholderNameRequired,
    getRecaptchaToken
  )
  const errMsg = makeErrMsg(errors.form)

  useEffect(() => {
    if (!kount || !kount.enabled) return
    const { clientId, environment } = kount
    initKount(clientId, environment, ({ SessionID }) =>
      setKountSessionId(SessionID)
    )
  }, [kount])

    // ml session_id
  useEffect(() => {
    const _sessionid = window.localStorage.getItem('ml_session_id')
    if (_sessionid) setMlSessionId(_sessionid)
  }, [])

  useEffect(() => {
    dispatch(resetCustomerCreditCardsError())
    return () => {
      dispatch(resetCustomerCreditCardsError())
    }
  }, [dispatch])

  useEffect(() => {
    if (!error) return
    if (windowRef.current) windowRef.current.scrollTop = 0
  }, [error, windowRef])

  useEffect(() => {
    if (!error && !errors) return

    const timeout = setTimeout(() => {
      const firstErrorElement =
        document.querySelector<HTMLInputElement>('.error')
      if (firstErrorElement) setTimeout(() => firstErrorElement.focus())
    })

    return () => clearTimeout(timeout)
  }, [error, errors])

  return (
    <form id="credit-card-form" onSubmit={handleSubmit} noValidate>
      <FormError errMsg={errMsg} style={{ margin: '0 0 2rem' }} />
      <CreditCard
        setCard={setNewCard}
        formErrors={errors}
        isCardholderNameRequired={isCardholderNameRequired}
      />
      <FormSubmit>
        <ButtonSubmit submitRef={submitRef} submitting={submitting}>
          {submitting ? submittingText : submitText}
        </ButtonSubmit>
      </FormSubmit>
      {isCardholderNameRequired && <FreedomPayContainer />}
    </form>
  )
}

export default CreditCardForm
